import { useState } from 'react';
import { XCircle, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PayPalSubscriptionButton } from './PayPalSubscriptionButton';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/lib/store';

interface SubscriptionModalProps {
  onClose: () => void;
  onSuccess: () => void;
  storeId: string;
}

export function SubscriptionModal({ onClose, onSuccess, storeId }: SubscriptionModalProps) {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore((state) => state.user);

  const handleUpgradeSuccess = () => {
    onSuccess();
  };

  const handleUpgradeError = (errorMessage: string) => {
    setError(errorMessage);
    setIsUpgrading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-medium text-gray-900">
              Mise à niveau vers l'abonnement Premium
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 space-y-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-6">
              <div className="flex items-center">
                <Crown className="h-8 w-8 text-yellow-300" />
                <h4 className="ml-2 text-xl font-bold">Abonnement Premium</h4>
              </div>
              <p className="mt-2">Débloquez tout le potentiel de votre boutique avec notre abonnement premium.</p>
              
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                  <div className="font-bold text-lg">25 produits</div>
                  <div className="text-sm">au lieu de 8</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                  <div className="font-bold text-lg">Priorité</div>
                  <div className="text-sm">dans les résultats de recherche</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                  <div className="font-bold text-lg">Support dédié</div>
                  <div className="text-sm">assistance prioritaire</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg">
                  <div className="font-bold text-lg">Statistiques</div>
                  <div className="text-sm">analyses avancées</div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Options de paiement</h4>
              
              <div className="mb-6">
                <div className="text-2xl font-bold text-gray-900">$15.00 <span className="text-sm font-normal text-gray-500">/ mois</span></div>
                <p className="text-sm text-gray-500 mt-1">Facturation mensuelle, annulez à tout moment</p>
              </div>
              
              {/* PayPal Subscription Button */}
              <PayPalSubscriptionButton 
                onSuccess={handleUpgradeSuccess}
                onError={handleUpgradeError}
              />
              
              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Contact option warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h5 className="text-sm font-semibold text-yellow-800 mb-2">Vous n'avez pas de carte ou de compte PayPal ?</h5>
              <p className="text-sm text-yellow-700 mb-3">
                Vous pouvez nous contacter pour souscrire à un abonnement Premium sans passer par PayPal :
              </p>
              <ul className="text-sm text-yellow-700 space-y-2">
                <li>• Email : <a href="mailto:aemfamily509@gmail.com" className="text-primary hover:text-primary-dark font-medium">aemfamily509@gmail.com</a></li>
                <li>• Téléphone : <a href="tel:+50947734531" className="text-primary hover:text-primary-dark font-medium">+509 47-73-45-31</a></li>
                <li>• International : <a href="tel:+15853629020" className="text-primary hover:text-primary-dark font-medium">+1 (585) 362-9020</a></li>
              </ul>
              <p className="text-sm text-yellow-700 mt-2">
                Appelez-nous ou écrivez-nous sur WhatsApp pour mettre en place votre abonnement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}